/**
 * The push token a listener handed over, held until the cloud has it.
 *
 * `services/pushNotifications.ts` owns the two FCM listeners that deliver this
 * token (Android's `registration`, iOS's `tokenReceived`) and the send that
 * registers it (`POST /api/devices/push-token`). One ordering between the two is
 * not safe on its own: on a cold launch the plugin can answer **before** the
 * cloud session is restored, and the send's own guard ("no cloud auth, no send")
 * would then drop that token for the whole session — a token that rotated in
 * that launch would stay unregistered until some later launch won the race, and
 * the cloud would keep calling the old one until it did (card `t_9801b8b2`).
 *
 * So a delivery is *retained* first and *accepted* only once the cloud really
 * has it, and "is anything still owed to the cloud?" is answered here. Kept pure
 * and on its own the same way `services/pushTarget.ts` is, because the module
 * that wires Capacitor cannot be imported by a test (plugin modules,
 * `import.meta.env`): this is the half a test can drive —
 * `pushTokenRetry.test.ts`.
 */

export interface PushTokenRetry {
  /**
   * Remember the token a listener just delivered — possibly with no cloud
   * session up, which is exactly the delivery a cold launch would otherwise
   * lose. What is owed follows from what was delivered, never from whether the
   * send was possible at the time.
   *
   * A delivery that carries no token (a listener can answer a blank value) is
   * not a delivery and changes nothing.
   */
  retain(token: string): void;
  /**
   * The cloud accepted this token: nothing is owed for it any more. Called only
   * after the send actually went through, so a send that threw leaves the token
   * owed for the next trigger.
   */
  accepted(token: string): void;
  /**
   * The token the cloud has not accepted yet, or `null` when nothing is owed —
   * no delivery yet, or the delivery is the token already registered.
   *
   * This is what the auth watcher in `pushNotifications.ts` re-sends the moment
   * a cloud session appears, and why the same token is not sent again by it.
   */
  pending(): string | null;
}

/** One process's memory of what was delivered and what the cloud has. */
export function createPushTokenRetry(): PushTokenRetry {
  let delivered: string | null = null;
  let registered: string | null = null;

  return {
    retain(token: string): void {
      const value = token.trim();
      if (value) delivered = value;
    },
    accepted(token: string): void {
      registered = token.trim();
    },
    pending(): string | null {
      if (!delivered || delivered === registered) return null;
      return delivered;
    },
  };
}
